/**
 * The site's one conversion control, and its only write: a visitor leaves an
 * address and is told, on the page, that they are on the beta list
 * (contracts/web-surface.md §3).
 *
 * ## Four states, exactly one visible
 *
 * idle → pending → success, with error as the retryable branch off pending.
 * The mutation owns three of them (`isPending`, `isError`, `isSuccess`) and a
 * single piece of local state owns the fourth — the client-side validation
 * message — so there is no second copy of "what is happening" to fall out of
 * step with the request. `subscribeEmail` rejects on every status >= 400 and on
 * a transport failure alike, which is what makes "the request failed" a single
 * observable rather than a status-code ladder this component would have to
 * interpret.
 *
 * Pending is enforced three ways because one of them is not enough: the field
 * and the button are both disabled so neither pointer nor keyboard reaches
 * them, and `submit` returns early regardless. The early return is what
 * actually stops a double-send — a disabled input still receives `keydown` in
 * the browser, so `onSubmitEditing` can fire while a request is in flight.
 *
 * ## Announcing without moving focus (FR-019(d))
 *
 * Nothing here calls `focus()`. The error and the confirmation are announced
 * because they land inside a live region, not because the caret is dragged to
 * them, which is the distinction FR-019(d) and acceptance scenario 1.8 draw.
 *
 * The live region is the wrapper that *swaps*, not the confirmation itself.
 * A live region only announces changes that happen while it is already in the
 * document; a region that appears together with its own content usually says
 * nothing at all. Wrapping the swap means the region is there from first paint,
 * so both changes inside it — the error text appearing, and the form giving way
 * to the confirmation — are read out. The initial render is not announced,
 * which is the behaviour we want.
 *
 * The error is additionally tied to the field with `aria-describedby`, so a
 * visitor who tabs back to it hears why it was rejected rather than only the
 * one announcement they may have missed.
 *
 * On success the form unmounts, so whatever held focus inside it goes with it —
 * the field on the keyboard path, the submit button on the pointer path — and
 * `document.activeElement` falls back to `<body>`. The T052 audit measured that
 * on both paths and left it alone deliberately. The only way to catch the
 * falling focus is to call `focus()` on the confirmation, which is exactly what
 * FR-019(d), contracts/web-surface.md §9(d) and T045 forbid: the confirmation is
 * announced, it does not take the caret. No WCAG 2.2 Level A or AA criterion
 * requires focus to survive the removal of the element holding it, and the audit
 * reported zero violations. The two controls that outlive the swap — the brand
 * link and the scheme toggle — are the first two stops in the tab order, so the
 * next Tab reaches them straight away.
 *
 * ## Notes on the two libraries underneath
 *
 * There is no `<form>` element: react-native-web renders a `<div>` around an
 * `<input>`, so there is no implicit submit. `onSubmitEditing` restores Enter,
 * and react-native-web calls `preventDefault` on it for us.
 *
 * Paper's `label` is decoration — it renders styled text, not a `<label for>` —
 * so the field's accessible name has to be given explicitly.
 *
 * `HelperText` is rendered only when there is something to say. Paper keeps a
 * `visible={false}` helper mounted at zero opacity, which leaves the message in
 * the accessibility tree permanently and would have the field described by an
 * error it does not have.
 */

import { useId, useState } from "react";
import { StyleSheet, View } from "react-native";
import { Button, HelperText, Text, TextInput } from "react-native-paper";
import { PrivacyNotice } from "@/components/marketing/privacy-notice";
import { useSubscribeEmail } from "@/lib/api-store";
import { validateEmail } from "@/lib/validate-email";
import {
  CONNECTION_FAILED_MESSAGE,
  FORM_MAX_WIDTH,
  invalidFieldProps,
} from "@/components/forms/form-shared";
import { useAppTheme } from "@/theme/paper-theme";
import { spacing } from "@/theme/tokens";

const FIELD_LABEL = "Email address";

/**
 * The visible label stays short so the button holds its shape down to a 320 px
 * viewport; the accessible name spells out what a press signs the visitor up
 * for. WCAG 2.5.3 asks the visible text to be *contained* in the accessible
 * name, which it is, so speech input still reaches the button by its label.
 */
const SUBMIT_LABEL = "Notify me";
const SUBMIT_ACCESSIBLE_NAME = "Notify me when the Sparkie beta opens";

/**
 * Says what is wrong with what they typed, not what an email address is. The
 * example is still there because "invalid format" tells nobody anything.
 */
const INVALID_EMAIL_MESSAGE =
  "That doesn't look like an email address. Something like name@example.com.";

const CONFIRMATION_MESSAGE =
  "You're on the list. We'll write when the beta opens.";

const RESET_LABEL = "Sign up another address";

export function EmailCaptureForm() {
  const theme = useAppTheme();
  const subscribe = useSubscribeEmail();
  const errorMessageId = useId();
  const [email, setEmail] = useState("");
  const [validationMessage, setValidationMessage] = useState<string | null>(
    null,
  );

  const errorMessage =
    validationMessage ?? (subscribe.isError ? CONNECTION_FAILED_MESSAGE : null);

  /**
   * Editing is the visitor answering the error, so the error goes quiet and the
   * form is idle again. The typed value is never touched by any of this — it is
   * the one thing a failed submission must keep (acceptance scenarios 1.3, 1.4).
   */
  function handleChangeText(value: string) {
    setEmail(value);
    setValidationMessage(null);
    if (subscribe.isError) {
      subscribe.reset();
    }
  }

  /**
   * Blur validation: flag a value that cannot succeed as soon as the visitor
   * moves on, not only after a submit. An empty field stays silent — leaving
   * without typing is not a mistake to scold.
   */
  function handleBlur() {
    if (email !== "" && !validateEmail(email)) {
      setValidationMessage(INVALID_EMAIL_MESSAGE);
    }
  }

  function handleSubmit() {
    if (subscribe.isPending) {
      return;
    }

    if (!validateEmail(email)) {
      subscribe.reset();
      setValidationMessage(INVALID_EMAIL_MESSAGE);
      return;
    }

    setValidationMessage(null);
    // Sent as typed. Trimming and lower-casing are the server's job (FR-004),
    // and a second normalizer here would only be something to drift from.
    subscribe.mutate(email);
  }

  return (
    <View accessibilityLiveRegion="polite" style={styles.region}>
      {subscribe.isSuccess ? (
        // The swap happens inside the live region, so both the confirmation and
        // the revived form announce. The reset control means the success state
        // is no longer terminal for the life of the page — a visitor can sign
        // up a second address without reloading.
        <View style={styles.form}>
          <Text variant="bodyLarge" style={{ color: theme.status.success }}>
            {CONFIRMATION_MESSAGE}
          </Text>
          <Button
            mode="text"
            onPress={() => {
              subscribe.reset();
              setEmail("");
            }}
          >
            {RESET_LABEL}
          </Button>
        </View>
      ) : (
        <View style={styles.form}>
          <TextInput
            mode="outlined"
            label={FIELD_LABEL}
            aria-label={FIELD_LABEL}
            value={email}
            onChangeText={handleChangeText}
            onBlur={handleBlur}
            onSubmitEditing={handleSubmit}
            // react-native-web blurs a single-line field after Enter, which is the
            // right instinct on a phone — it puts the keyboard away — and the wrong
            // one here. It matters on the error path, where the field survives the
            // submit: a visitor whose caret dropped to `<body>` would have to tab
            // back to the value they still have to correct. It cannot help on the
            // success path, where the field is unmounted (see the FR-019(d) note
            // above).
            blurOnSubmit={false}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="go"
            disabled={subscribe.isPending}
            error={errorMessage !== null}
            // The id is per-instance (`useId`): the page mounts this form
            // twice, and a shared literal id once made `aria-describedby`
            // resolve to the other form's message.
            {...(errorMessage !== null
              ? invalidFieldProps(errorMessageId)
              : {})}
          />

          {errorMessage !== null ? (
            <HelperText type="error" visible id={errorMessageId}>
              {errorMessage}
            </HelperText>
          ) : null}

          <PrivacyNotice />

          <Button
            mode="contained"
            onPress={handleSubmit}
            loading={subscribe.isPending}
            disabled={subscribe.isPending}
            accessibilityLabel={SUBMIT_ACCESSIBLE_NAME}
          >
            {SUBMIT_LABEL}
          </Button>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  region: {
    maxWidth: FORM_MAX_WIDTH,
    width: "100%",
  },
  form: {
    // The helper text carries its own top padding, so the gap here is the one
    // between the field, the notice and the button.
    gap: spacing.sm,
  },
});
